import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';

const STATUS_COLORS = {
  new:        { bg: '#fef9c3', color: '#854d0e', label: '🆕 New' },
  reviewing:  { bg: '#dbeafe', color: '#1e40af', label: '👀 Reviewing' },
  confirmed:  { bg: '#d1fae5', color: '#065f46', label: '✅ Confirmed' },
  declined:   { bg: '#fee2e2', color: '#991b1b', label: '❌ Declined' },
};

export default function Catering() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const toast = useToast();

  useEffect(() => {
    api.get('/restaurants').then(data => {
      if (data.length > 0) {
        setRestaurant(data[0]);
        // Check Pro plan
        if ((data[0].plan || 'basic') !== 'pro') {
          setLoading(false);
          return;
        }
        loadRequests(data[0].restaurant_id);
      } else {
        setLoading(false);
      }
    }).catch(() => setLoading(false));
  }, []);

  const loadRequests = (rid) => {
    setLoading(true);
    api.get(`/restaurants/${rid}/catering`)
      .then(setRequests)
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await api.put(`/restaurants/${restaurant.restaurant_id}/catering/${requestId}/status`, { status: newStatus });
      setRequests(prev => prev.map(r => r.request_id === requestId ? { ...r, status: newStatus } : r));
      toast.success(`Request status updated to ${newStatus}!`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const statusCounts = requests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  const filtered = filterStatus === 'all' ? requests : requests.filter(r => r.status === filterStatus);

  if (loading) return <AdminLayout title="Catering Requests"><div className="loading-center"><div className="loading-spinner"></div></div></AdminLayout>;

  // Pro plan wall
  if (restaurant && (restaurant.plan || 'basic') !== 'pro') {
    return (
      <AdminLayout title="Catering Requests">
        <div style={{ maxWidth: 600, margin: '60px auto', textAlign: 'center', padding: '0 24px' }}>
          <div style={{ fontSize: 72, marginBottom: 20 }}>🔒</div>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>Catering is a Pro Feature</h2>
          <p style={{ fontSize: 16, color: '#666', marginBottom: 32 }}>Upgrade to Pro to manage catering requests from customers.</p>
          <a href="/admin/settings" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#0f1117', padding: '14px 40px', borderRadius: 12, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}>
            ⭐ Upgrade to Pro in Settings
          </a>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Catering Requests">
      <div style={{ maxWidth: 900 }}>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total', value: requests.length, icon: '🎪' },
            { label: 'New', value: statusCounts.new || 0, icon: '🆕', highlight: (statusCounts.new || 0) > 0 },
            { label: 'Confirmed', value: statusCounts.confirmed || 0, icon: '✅' },
            { label: 'Declined', value: statusCounts.declined || 0, icon: '❌' },
          ].map(stat => (
            <div key={stat.label} className="card" style={{
              padding: '16px', textAlign: 'center',
              border: stat.highlight ? '2px solid #f97316' : undefined,
              background: stat.highlight ? '#fff7ed' : undefined,
            }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{stat.icon}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: stat.highlight ? '#ea580c' : 'var(--primary)' }}>{stat.value}</div>
              <div style={{ fontSize: 12, color: '#666' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          {['all', ...Object.keys(STATUS_COLORS)].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', border: '1px solid var(--border)',
              background: filterStatus === s ? 'var(--primary)' : 'white',
              color: filterStatus === s ? 'white' : '#555',
            }}>
              {s === 'all' ? `All (${requests.length})` : `${STATUS_COLORS[s]?.label} (${statusCounts[s] || 0})`}
            </button>
          ))}
        </div>

        {/* Requests List */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎪</div>
            <h3>{requests.length === 0 ? 'No catering requests yet' : 'No requests with this status'}</h3>
            <p>{requests.length === 0 ? 'Catering requests from customers will appear here' : 'Try a different filter'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {filtered.map(req => {
              const statusInfo = STATUS_COLORS[req.status] || STATUS_COLORS.new;
              const isExpanded = expanded === req.request_id;
              const isNew = req.status === 'new';

              return (
                <div key={req.request_id} className="card" style={{
                  overflow: 'hidden',
                  border: isNew ? '2px solid #f97316' : undefined,
                }}>
                  {isNew && (
                    <div style={{ background: '#f97316', color: 'white', fontSize: 11, fontWeight: 700, padding: '3px 12px', textAlign: 'center', letterSpacing: 1 }}>
                      🆕 NEW REQUEST
                    </div>
                  )}

                  {/* Header */}
                  <div onClick={() => setExpanded(isExpanded ? null : req.request_id)}
                    style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{req.name}</div>
                      <div style={{ fontSize: 13, color: '#666' }}>
                        📅 {req.event_date} &nbsp;|&nbsp; 👥 {req.guest_count} guests
                        {req.budget && <>&nbsp;|&nbsp; 💰 {req.budget}</>}
                      </div>
                    </div>
                    <span style={{ background: statusInfo.bg, color: statusInfo.color, padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {statusInfo.label}
                    </span>
                    <div style={{ textAlign: 'right', marginRight: 8 }}>
                      <div style={{ fontSize: 13, color: '#888' }}>{req.phone}</div>
                      <div style={{ fontSize: 12, color: '#aaa' }}>{req.email}</div>
                    </div>
                    <span style={{ fontSize: 18, color: '#999', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.2s' }}>▾</span>
                  </div>

                  {/* Quick action for new requests */}
                  {isNew && !isExpanded && (
                    <div style={{ padding: '0 18px 14px', display: 'flex', gap: 8 }}>
                      <button onClick={() => updateStatus(req.request_id, 'confirmed')} style={{ flex: 1, padding: '8px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ✅ Confirm
                      </button>
                      <button onClick={() => updateStatus(req.request_id, 'reviewing')} style={{ flex: 1, padding: '8px', background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        👀 Review
                      </button>
                      <button onClick={() => updateStatus(req.request_id, 'declined')} style={{ flex: 1, padding: '8px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                        ❌ Decline
                      </button>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div style={{ background: 'var(--primary-light)', borderRadius: 10, padding: '12px 16px' }}>
                          <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--primary)' }}>👤 Contact Info</h4>
                          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div><strong>Name:</strong> {req.name}</div>
                            <div><strong>Phone:</strong> {req.phone}</div>
                            <div><strong>Email:</strong> {req.email}</div>
                          </div>
                        </div>
                        <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 16px' }}>
                          <h4 style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--primary)' }}>🎪 Event Details</h4>
                          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div><strong>Date:</strong> {req.event_date}</div>
                            <div><strong>Guests:</strong> {req.guest_count}</div>
                            {req.event_type && <div><strong>Type:</strong> {req.event_type}</div>}
                            {req.budget && <div><strong>Budget:</strong> {req.budget}</div>}
                          </div>
                        </div>
                      </div>

                      {req.menu_preferences && (
                        <div style={{ background: '#eff6ff', borderRadius: 10, padding: '12px 16px', marginBottom: 12, fontSize: 13 }}>
                          <strong>🍽️ Menu Preferences:</strong> {req.menu_preferences}
                        </div>
                      )}
                      {req.notes && (
                        <div style={{ background: '#fef9c3', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
                          <strong>📝 Notes:</strong> {req.notes}
                        </div>
                      )}

                      <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
                        🕐 Submitted: {new Date(req.created_at).toLocaleString()}
                      </div>

                      {/* Status Actions */}
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Update Status:</p>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {Object.entries(STATUS_COLORS).map(([status, info]) => (
                            <button key={status}
                              onClick={() => updateStatus(req.request_id, status)}
                              disabled={req.status === status}
                              style={{
                                padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                cursor: req.status === status ? 'default' : 'pointer',
                                border: '1px solid', borderColor: info.color,
                                background: req.status === status ? info.bg : 'white',
                                color: info.color, opacity: req.status === status ? 1 : 0.7,
                              }}>
                              {info.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {restaurant && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <button className="btn btn-outline" onClick={() => loadRequests(restaurant.restaurant_id)}>
              🔄 Refresh
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}